import { CollectionData, PaginationResults } from "@/lib/type";
import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithLogout } from "@/lib/baseQuery";
import {
  PurchaseOrder,
  PurchaseOrderStatus,
} from "./inventory.type";
import { IngredientApi } from "./ingredient.api";
import { StockAdjustmentApi } from "./stockAdjustment.api";
import { ExpenseApi } from "@/redux/services/expense/expense.api";
import { RegisterSessionApi } from "@/redux/services/pos/registerSession.api";
import {
  ReportDownload,
  parseContentDispositionFilename,
} from "@/lib/reportDownload";

export interface PurchaseOrderLineInput {
  ingredient: string;
  quantityOrdered?: string;
  unitPrice?: number;
  packaging?: string;
  packageQuantityOrdered?: string;
  packageUnitPrice?: number;
}

export interface BulkPurchaseOrderInput {
  supplier: string;
  orderDate?: string;
  expectedDeliveryDate?: string;
  notes?: string | null;
  lines: PurchaseOrderLineInput[];
}

export interface BulkPurchaseOrderCreatePayload {
  orders: BulkPurchaseOrderInput[];
}

export interface PurchaseOrderPatchPayload {
  supplier?: string;
  orderDate?: string;
  expectedDeliveryDate?: string | null;
  notes?: string | null;
  status?: PurchaseOrderStatus;
}

export interface ReceivedLineInput {
  lineUuid: string;
  receivedQuantity?: string;
  packageQuantityReceived?: string;
}

export type PurchaseOrderSortField =
  | "orderDate"
  | "totalAmount"
  | "receivedAt";

export interface GetPurchaseOrdersParams {
  page?: number;
  limit?: number;
  status?: PurchaseOrderStatus;
  supplierUuid?: string;
  search?: string;
  orderDateAfter?: string;
  orderDateBefore?: string;
  sortBy?: PurchaseOrderSortField;
  sortDirection?: "asc" | "desc";
}

export const PurchaseOrderApi = createApi({
  reducerPath: "purchaseOrderApi",
  tagTypes: ["purchaseOrder"],
  baseQuery: baseQueryWithLogout,
  endpoints: (build) => ({
    getPurchaseOrders: build.query<
      PaginationResults<PurchaseOrder>,
      { restaurantId: string; query?: GetPurchaseOrdersParams }
    >({
      query: ({ restaurantId, query }) => ({
        url: `restaurants/${restaurantId}/purchase_orders`,
        method: "GET",
        params: {
          ...(query?.page !== undefined ? { page: query.page } : {}),
          ...(query?.limit !== undefined ? { limit: query.limit } : {}),
          ...(query?.status ? { status: query.status } : {}),
          ...(query?.supplierUuid
            ? { "supplier.uuid": query.supplierUuid }
            : {}),
          ...(query?.search ? { search: query.search } : {}),
          ...(query?.orderDateAfter
            ? { "orderDate[after]": query.orderDateAfter }
            : {}),
          ...(query?.orderDateBefore
            ? { "orderDate[before]": query.orderDateBefore }
            : {}),
          ...(query?.sortBy && query?.sortDirection
            ? { [`order[${query.sortBy}]`]: query.sortDirection }
            : {}),
        },
      }),
      providesTags: ["purchaseOrder"],
      transformResponse: (data: CollectionData<PurchaseOrder>) => ({
        results: data["hydra:member"],
        count: data["hydra:totalItems"],
      }),
    }),

    getPurchaseOrder: build.query<PurchaseOrder, { uuid: string }>({
      query: ({ uuid }) => `purchase_orders/${uuid}`,
      providesTags: ["purchaseOrder"],
    }),

    bulkCreatePurchaseOrders: build.mutation<
      PurchaseOrder[],
      { restaurantId: string; data: BulkPurchaseOrderCreatePayload }
    >({
      query: ({ restaurantId, data }) => ({
        url: `restaurants/${restaurantId}/purchase_orders/bulk`,
        method: "POST",
        body: data,
      }),
      // 201 returns a plain JSON array of the created POs (no Hydra wrapper)
      invalidatesTags: ["purchaseOrder"],
      // Bulk-created orders arrive directly as "received": stock moves too
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(IngredientApi.util.invalidateTags(["ingredient"]));
          dispatch(
            StockAdjustmentApi.util.invalidateTags(["stockAdjustment"])
          );
        } catch {
          // ignore
        }
      },
    }),

    updatePurchaseOrder: build.mutation<
      PurchaseOrder,
      { uuid: string; data: PurchaseOrderPatchPayload }
    >({
      query: ({ uuid, data }) => ({
        url: `purchase_orders/${uuid}`,
        method: "PATCH",
        headers: { "Content-Type": "application/merge-patch+json" },
        body: JSON.stringify(data),
      }),
      invalidatesTags: ["purchaseOrder"],
    }),

    deletePurchaseOrder: build.mutation<void, { uuid: string }>({
      query: ({ uuid }) => ({
        url: `purchase_orders/${uuid}`,
        method: "DELETE",
      }),
      invalidatesTags: ["purchaseOrder"],
    }),

    // Accountant export: one CSV row per received line over a date range.
    // Both dates inclusive (YYYY-MM-DD), range <= 365 days. text/csv body.
    getPurchaseReportCsv: build.query<
      ReportDownload,
      { restaurantId: string; startDate: string; endDate: string }
    >({
      query: ({ restaurantId, startDate, endDate }) => ({
        url: `restaurants/${restaurantId}/purchase_report`,
        method: "GET",
        params: { start_date: startDate, end_date: endDate },
        responseHandler: async (response: Response) => ({
          blob: await response.blob(),
          filename: parseContentDispositionFilename(response),
        }),
      }),
    }),

    getPurchaseOrderPdf: build.query<Blob, { uuid: string }>({
      query: ({ uuid }) => ({
        url: `purchase_orders/${uuid}/pdf`,
        method: "GET",
        responseHandler: async (response: Response) => response.blob(),
      }),
    }),

    receivePurchaseOrder: build.mutation<
      PurchaseOrder,
      { uuid: string; data: { receivedLines: ReceivedLineInput[] } }
    >({
      query: ({ uuid, data }) => ({
        url: `purchase_orders/${uuid}/receive`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["purchaseOrder"],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(IngredientApi.util.invalidateTags(["ingredient"]));
          dispatch(
            StockAdjustmentApi.util.invalidateTags(["stockAdjustment"])
          );
        } catch {
          // ignore
        }
      },
    }),

    // Cancel a received / partially_received PO. Unlike the draft/sent status
    // PATCH, this reverses the stock that was already received, so refresh the
    // ingredient and stock-adjustment caches.
    cancelPurchaseOrder: build.mutation<PurchaseOrder, { uuid: string }>({
      query: ({ uuid }) => ({
        url: `purchase_orders/${uuid}/cancel`,
        method: "POST",
      }),
      invalidatesTags: ["purchaseOrder"],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(IngredientApi.util.invalidateTags(["ingredient"]));
          dispatch(
            StockAdjustmentApi.util.invalidateTags(["stockAdjustment"])
          );
        } catch {
          // ignore
        }
      },
    }),
    // Mark a PO as paid. Optionally generates a COGS expense, so refresh the
    // expense caches (and the financial overview) cross-slice.
    payPurchaseOrder: build.mutation<
      PurchaseOrder,
      {
        uuid: string;
        paymentMethod: string;
        paidFromDrawer?: boolean;
        generateExpense?: boolean;
      }
    >({
      query: ({ uuid, paymentMethod, paidFromDrawer, generateExpense }) => ({
        url: `purchase_orders/${uuid}/pay`,
        method: "POST",
        body: {
          paymentMethod,
          paidFromDrawer: paidFromDrawer ?? false,
          generateExpense: generateExpense ?? true,
        },
      }),
      invalidatesTags: ["purchaseOrder"],
      async onQueryStarted(
        { paidFromDrawer },
        { dispatch, queryFulfilled }
      ) {
        try {
          await queryFulfilled;
          dispatch(ExpenseApi.util.invalidateTags(["expense"]));
          // A drawer-backed payment records a cash pay-out movement on the
          // open session; refresh the session + ledger caches so an open
          // cash-register view doesn't show stale data (mirrors
          // createExpense / voidExpense).
          if (paidFromDrawer) {
            dispatch(
              RegisterSessionApi.util.invalidateTags([
                "registerSession",
                "registerSessionLedger",
              ])
            );
          }
        } catch {
          // ignore
        }
      },
    }),
  }),
});

export const {
  useGetPurchaseOrdersQuery,
  useGetPurchaseOrderQuery,
  useBulkCreatePurchaseOrdersMutation,
  useUpdatePurchaseOrderMutation,
  useDeletePurchaseOrderMutation,
  useReceivePurchaseOrderMutation,
  useCancelPurchaseOrderMutation,
  usePayPurchaseOrderMutation,
  useLazyGetPurchaseOrderPdfQuery,
  useLazyGetPurchaseReportCsvQuery,
} = PurchaseOrderApi;
